import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Link2, Upload, Trash2, ExternalLink, Loader2, CheckCircle2, ClipboardCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { uploadChunked, mediaUrl } from "@/lib/social";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ApproveBriefDialog } from "@/components/social/ApproveBriefDialog";

const hrefOf = (it) => (it.kind === "link" ? it.url : `${mediaUrl(it.media_id)}?k=${it.sig}`);

function BriefItem({ it, canEdit, onRemove }) {
  const Icon = it.kind === "link" ? Link2 : FileText;
  return (
    <div className="group flex items-center gap-2 rounded-xl border border-[#EEEFEF] bg-white hover:border-[#d9ccff] transition-colors">
      <a href={hrefOf(it)} target="_blank" rel="noopener noreferrer" data-testid={`brief-item-${it.id}`} className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2.5">
        <span className="h-8 w-8 shrink-0 rounded-lg bg-[#F7F4FF] text-[#5b2fd6] flex items-center justify-center"><Icon className="h-4 w-4" /></span>
        <span className="min-w-0">
          <span className="block text-sm text-[#06060C] truncate">{it.title}</span>
          <span className="block text-[11px] text-[#9FA1A4] truncate">{it.kind === "link" ? it.url.replace(/^https?:\/\//, "") : "Uploaded file"} · {it.by_name}</span>
        </span>
        <ExternalLink className="h-3.5 w-3.5 ml-auto shrink-0 text-[#9FA1A4] group-hover:text-[#5b2fd6]" />
      </a>
      {canEdit && <button data-testid={`brief-remove-${it.id}`} onClick={() => onRemove(it)} className="mr-2 h-8 w-8 rounded-full flex items-center justify-center text-[#9FA1A4] hover:text-rose-600 hover:bg-rose-50" aria-label="Remove"><Trash2 className="h-4 w-4" /></button>}
    </div>
  );
}

export function BriefPanel({ d, reload }) {
  const f = d.flow;
  const gid = d.group.id;
  const items = d.group.brief_items || [];
  const can = f.can_brief;
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [pct, setPct] = useState(null);
  const [approve, setApprove] = useState(false);
  const fileRef = useRef(null);
  const err = (e) => toast.error(formatApiError(e.response?.data?.detail));
  const addLink = async () => {
    try { await api.post(`/console/social/groups/${gid}/brief/link`, { url: url.trim(), title: title.trim() }); setUrl(""); setTitle(""); toast.success("Brief link saved"); reload(); } catch (e) { err(e); }
  };
  const upload = async (file) => {
    if (!file) return;
    setPct(0);
    try { await uploadChunked(`/console/social/groups/${gid}/brief/init`, file, setPct); toast.success("Brief file uploaded"); reload(); } catch (e) { err(e); } finally { setPct(null); if (fileRef.current) fileRef.current.value = ""; }
  };
  const remove = async (it) => { try { await api.delete(`/console/social/groups/${gid}/brief/${it.id}`); reload(); } catch (e) { err(e); } };
  const step = (f.steps || []).find((s) => s.social_step === "brief" && s.status !== "Completed");
  return (
    <section className="rounded-2xl border border-[#06060C]/10 bg-[#FBFAFF] p-4 sm:p-5 space-y-3" data-testid="social-brief-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <ClipboardCheck className="h-4 w-4 text-[#5b2fd6]" />
        <p className="text-sm font-medium text-[#06060C]">Brief & strategy</p>
        <span className="text-xs text-[#9FA1A4]">{items.length ? `${items.length} item${items.length === 1 ? "" : "s"} · opens in a new window` : f.stage === "brief" ? "Add the brief before approving it" : "No brief materials were added"}</span>
        {f.can_approve_brief && step && <Button data-testid="brief-approve-open" disabled={!items.length} onClick={() => setApprove(true)} className="ml-auto rounded-full bg-[#06060C] hover:bg-[#1c1c24] text-white"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve brief</Button>}
      </div>
      {items.length > 0 && <div className="grid sm:grid-cols-2 gap-2" data-testid="brief-items">{items.map((it) => <BriefItem key={it.id} it={it} canEdit={can} onRemove={remove} />)}</div>}
      {can && (
        <div className="flex flex-col md:flex-row gap-2">
          <Input data-testid="brief-link-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste a Google Doc, Sheet or other link" className="bg-white md:flex-[2]" />
          <Input data-testid="brief-link-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Name (optional)" className="bg-white md:flex-1" />
          <Button data-testid="brief-link-add" variant="outline" disabled={!url.trim()} onClick={addLink} className="rounded-full border-[#EEEFEF]"><Link2 className="h-4 w-4 mr-1.5" /> Save link</Button>
          <input ref={fileRef} type="file" className="hidden" data-testid="brief-file-input" onChange={(e) => upload(e.target.files?.[0])} />
          <Button data-testid="brief-upload" variant="outline" disabled={pct !== null} onClick={() => fileRef.current?.click()} className="rounded-full border-[#EEEFEF]">
            {pct !== null ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> {pct}%</> : <><Upload className="h-4 w-4 mr-1.5" /> Upload brief</>}
          </Button>
        </div>
      )}
      {approve && step && f.can_approve_brief && <ApproveBriefDialog step={step} mainId={f.main_task_id} onClose={() => setApprove(false)} />}
    </section>
  );
}
